import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import en from "./locales/en.json";

// Non-English packs (UI strings + machine-translated content packs) are code-split and
// loaded on demand so the main bundle only carries English (~470 KB saved on first load).
// English is the source of truth and lives in the data files (passed as t() defaultValue).
const PACKS = {
  es: () => Promise.all([import("./locales/es.json"), import("./content/es.json")]),
  fr: () => Promise.all([import("./locales/fr.json"), import("./content/fr.json")]),
  zh: () => Promise.all([import("./locales/zh.json"), import("./content/zh.json")]),
};
const baseOf = (lng) => (lng || "en").split("-")[0];

export async function loadLocale(lng) {
  const base = baseOf(lng);
  if (!PACKS[base] || i18n.hasResourceBundle(base, "translation")) return base;
  const [ui, content] = await PACKS[base]();
  // Deep-merge (deep=true) so content-pack keys that share a top-level namespace with UI keys (e.g. "lab") don't drop them.
  i18n.addResourceBundle(base, "translation", ui.default, true, true);
  i18n.addResourceBundle(base, "translation", content.default, true, true);
  return base;
}

// Load the pack first, then switch — avoids a flash of raw keys / English.
export async function setLanguage(lng) {
  const base = await loadLocale(lng);
  return i18n.changeLanguage(base);
}

// Registry of supported languages — add a new entry + locale file to support more.
export const LANGUAGES = [
  { code: "en", label: "English", native: "English" },
  { code: "es", label: "Spanish", native: "Español" },
  { code: "fr", label: "French", native: "Français" },
  { code: "zh", label: "Mandarin", native: "中文" },
];

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: { en: { translation: en } },
    partialBundledLanguages: true,
    fallbackLng: "en",
    supportedLngs: LANGUAGES.map((l) => l.code),
    nonExplicitSupportedLngs: true,
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "navigator", "htmlTag"],
      lookupLocalStorage: "fab_lang",
      caches: ["localStorage"],
    },
  });

const applyHtmlLang = (lng) => {
  const base = (lng || "en").split("-")[0];
  document.documentElement.lang = base;
  document.documentElement.classList.toggle("lang-zh", base === "zh");
};
applyHtmlLang(i18n.resolvedLanguage || i18n.language);
i18n.on("languageChanged", applyHtmlLang);

// Returning visitors with a non-English preference: fetch their pack right away.
export const localeReady = PACKS[baseOf(i18n.language)] ? setLanguage(i18n.language) : Promise.resolve();

export default i18n;
